import {debounce} from 'lodash';
import {useCallback, useEffect, useRef, useState} from 'react';

type UpdateFn<T> = (data: T) => Promise<void> | void;
type TransformFn<T> = (data: T) => T;

// Status of optimistic updates - tracks the current state of server synchronization
export enum OptimisticUpdateStatus {
  IDLE = 'idle', // No active updates or errors
  PENDING = 'pending', // Server update in progress
  ERROR = 'error', // Last server update failed
}

export interface UseOptimisticUpdateOptions<T> {
  dataFromServer: T; // Initial data state
  debounceMs?: number; // Debounce delay in ms (default: 500)
  onError?: (error: unknown) => void; // Callback when server update fails
  onSuccess?: (data: T) => void; // Callback when server update succeeds
  transformBeforeSave?: TransformFn<T>; // Optional transform before saving to server
  updateFn: UpdateFn<T>; // Function that persists data to server
}

export interface UseOptimisticUpdateResult<T> {
  optimisticData: T; // Current UI state (may be ahead of server)
  serverData: T; // Last confirmed server state
  status: OptimisticUpdateStatus; // Current status of server sync
  updateOptimistically: (newData: Partial<T> | ((prev: T) => T)) => void; // Update UI immediately and schedule server update
  resetToServerState: () => void; // Reset UI state to match server state
}

/**
 * A hook for optimistic UI updates with debounced server persistence and status tracking.
 *
 * Features:
 * - Immediate UI updates for responsive UX
 * - Debounced server updates to reduce API calls
 * - Status tracking (idle, pending, error)
 * - Error handling with automatic rollback
 * - Pending changes saved on unmount
 *
 * @example
 * ```tsx
 * const {
 *   optimisticData,         // Current UI state
 *   updateOptimistically,   // Update function
 *   status,                 // OptimisticUpdateStatus enum
 *   resetToServerState      // Reset function
 * } = useOptimisticUpdate({
 *   initialData: { title: 'Initial', count: 0 },
 *   updateFn: async (data) => await api.update(itemId, data),
 *   debounceMs: 500
 * });
 *
 * // Show loading state
 * if (status === OptimisticUpdateStatus.PENDING) {
 *   return <Spinner />;
 * }
 *
 * // Show error
 * if (status === OptimisticUpdateStatus.ERROR) {
 *   return <ErrorMessage onRetry={() => resetToServerState()} />;
 * }
 *
 * // Example usage
 * const handleChange = (e) => {
 *   updateOptimistically({ title: e.target.value });
 * };
 * ```
 */

export function useOptimisticUpdate<T extends Record<string, any>>({
  dataFromServer,
  debounceMs = 500,
  onError,
  onSuccess,
  transformBeforeSave,
  updateFn,
}: UseOptimisticUpdateOptions<T>): UseOptimisticUpdateResult<T> {
  // Track both the optimistic state (shown to user) and server state (last known good state)
  const [optimisticData, setOptimisticData] = useState<T>(dataFromServer);
  const [serverData, setServerData] = useState<T>(dataFromServer);
  const [status, setStatus] = useState<OptimisticUpdateStatus>(
    OptimisticUpdateStatus.IDLE
  );

  // Keep reference for tracking pending updates
  const pendingDataRef = useRef<T | null>(null);

  // Keep server data in sync with initialData if it changes externally
  useEffect(() => {
    setServerData(dataFromServer);
    setOptimisticData(dataFromServer);
  }, [dataFromServer]);

  // Handle saving data to the server
  const saveToServer = useCallback(
    async (dataToSave: T) => {
      try {
        setStatus(OptimisticUpdateStatus.PENDING);

        // Apply transform if provided
        const transformedData = transformBeforeSave
          ? transformBeforeSave(dataToSave)
          : dataToSave;

        // Perform the actual update
        await updateFn(transformedData);

        // Update server data on success
        setServerData(dataToSave);
        setStatus(OptimisticUpdateStatus.IDLE);
        onSuccess?.(dataToSave);
      } catch (error) {
        // On error, revert optimistic state to server state
        setOptimisticData(serverData);
        setStatus(OptimisticUpdateStatus.ERROR);
        onError?.(error);

        // Also clear any pending state
        pendingDataRef.current = null;
      }
    },
    [updateFn, transformBeforeSave, serverData, onSuccess, onError]
  );

  // Create a debounced version of saveToServer
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSaveToServer = useCallback(
    debounce((data: T) => {
      saveToServer(data);
      pendingDataRef.current = null;
    }, debounceMs),
    [debounceMs, saveToServer]
  );

  // Update optimistically with debouncing
  const updateOptimistically = useCallback(
    (update: Partial<T> | ((prev: T) => T)) => {
      // Calculate new state
      const newData =
        typeof update === 'function'
          ? (update as (prev: T) => T)(optimisticData)
          : {...optimisticData, ...update};

      // Apply optimistic update immediately
      setOptimisticData(newData);

      // Store for pending update
      pendingDataRef.current = newData;

      // Cancel previous debounced call to avoid race conditions
      debouncedSaveToServer.cancel();

      // Trigger debounced save with the latest data
      debouncedSaveToServer(newData);
    },
    [optimisticData, debouncedSaveToServer]
  );

  // Reset optimistic state to match server state
  const resetToServerState = useCallback(() => {
    setOptimisticData(serverData);
    pendingDataRef.current = null;

    // Cancel any pending debounced updates
    debouncedSaveToServer.cancel();
  }, [serverData, debouncedSaveToServer]);

  // Clean up on unmount - process any pending changes
  useEffect(() => {
    return () => {
      // Cancel any pending debounced calls
      debouncedSaveToServer.cancel();

      // If there are any pending changes when unmounting, send them to the server
      if (pendingDataRef.current) {
        saveToServer(pendingDataRef.current);
        pendingDataRef.current = null;
      }
    };
  }, [saveToServer, debouncedSaveToServer]);

  return {
    optimisticData,
    serverData,
    updateOptimistically,
    status,
    resetToServerState,
  };
}
